import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/Button/Button';
import deleteIcon from '../../../image-assets/icon.delete.svg';
import { ToneSection } from '../types';
import './ToneRulesSection.css';

interface ToneRulesSectionProps {
  section: ToneSection;
  canEdit: boolean;
  isAddingSection: boolean;
  onSectionError: (error: string | null) => void;
}

const ToneRulesSection: React.FC<ToneRulesSectionProps> = ({
  section,
  canEdit,
  isAddingSection,
  onSectionError
}) => {
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [newRule, setNewRule] = useState<boolean>(false);
  const [clickedRuleInfo, setClickedRuleInfo] = useState<number | null>(null);
  const [sectionRules, setSectionRules] = useState<string[]>(section.rules);
  
  // Update local rules when section rules change from parent
  useEffect(() => {
    setSectionRules(section.rules);
  }, [section.rules]);

  const ruleRefs = useRef<{[key: string]: HTMLDivElement}>({});
  
  const getRuleRefKey = (index: number) => `${section.id}-${index}`;

  const handleRuleClick = (index: number, currentValue: string) => {
    if (!canEdit || isAddingSection) return;
    
    setEditingRuleIndex(index);
    
    setTimeout(() => {
      const refKey = getRuleRefKey(index);
      const ruleElement = ruleRefs.current[refKey];
      if (ruleElement) {
        ruleElement.focus();
        
        if (document.createRange && window.getSelection) {
          const range = document.createRange();
          range.selectNodeContents(ruleElement);
          range.collapse(false);
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }
    }, 10);
  };

  const handleRuleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const value = event.currentTarget.innerText;
      
      const updatedRules = [...sectionRules];
      updatedRules[index] = value;
      setSectionRules(updatedRules);
      
      event.currentTarget.blur();
    }
  };

  const handleRuleBlur = async (
    index: number, 
    value: string,
    isClickingAnotherRule = false
  ) => {
    if (!canEdit) return;

    const updatedRules = [...sectionRules];
    updatedRules[index] = value;
    setSectionRules(updatedRules);

    if (!isClickingAnotherRule) {
      setEditingRuleIndex(null);
    }
    
    setNewRule(false);

    try {
      const { error: updateError } = await supabase
        .from('ToneSections')
        .update({
          rules: updatedRules,
        })
        .eq('id', section.id);

      if (updateError) throw updateError;
    } catch (err: any) {
      console.error(`Error saving rule ${index} in section ${section.id}:`, err);
      onSectionError(`Failed to save rule: ${err.message}`);
    }
  };

  const handleDeleteRule = async (ruleIndex: number) => {
    if (!canEdit) return;

    const updatedRules = sectionRules.filter((_, index) => index !== ruleIndex);
    setSectionRules(updatedRules);

    try {
      const { error: updateError } = await supabase
        .from('ToneSections')
        .update({
          rules: updatedRules,
        })
        .eq('id', section.id);

      if (updateError) throw updateError;
    } catch (err: any) {
      console.error(`Error deleting rule from section ${section.id}:`, err);
      onSectionError(`Failed to delete rule: ${err.message}`);
    }
  };

  const handleAddRuleFromInput = async (value: string) => {
    if (!value.trim()) return;
    
    const updatedRules = [...sectionRules, value.trim()];
    setSectionRules(updatedRules);
    
    try {
      const { error: updateError } = await supabase
        .from('ToneSections')
        .update({
          rules: updatedRules,
        })
        .eq('id', section.id);

      if (updateError) throw updateError;
    } catch (err: any) {
      console.error(`Error saving rules for section ${section.id}:`, err);
      onSectionError(`Failed to save rules: ${err.message}`);
    }
  };

  return (
    <div className="tone-section-rules">
      <div className="list-container">
        {sectionRules.length > 0 ? (
          sectionRules.map((rule, index) => (
            <div 
              key={index} 
              className="list-item"
              onClick={(e) => {
                e.preventDefault();
                
                if (canEdit && !isAddingSection) {
                  setClickedRuleInfo(index);
                  handleRuleClick(index, rule);
                }
              }}
            >
              <div className="rule-item list-item-inset">
                {(editingRuleIndex === index) ? (
                  <div
                    className="rule-content"
                    contentEditable={true}
                    suppressContentEditableWarning={true}
                    ref={(el) => {
                      if (el) {
                        ruleRefs.current[getRuleRefKey(index)] = el;
                      }
                    }}
                    onKeyDown={(e) => handleRuleKeyDown(index, e)}
                    onBlur={(e) => {
                      const isClickingAnotherRule = clickedRuleInfo !== null && clickedRuleInfo !== index;
                      
                      handleRuleBlur(index, e.currentTarget.innerText, isClickingAnotherRule);
                      
                      setClickedRuleInfo(null);
                    }}
                    dangerouslySetInnerHTML={{ __html: rule }}
                  />
                ) : (
                  <>
                    <div 
                      className="rule-content"
                      contentEditable={false}
                      suppressContentEditableWarning={true}
                      dangerouslySetInnerHTML={{ __html: rule }}
                    />
                    <div className="delete-button-container">
                      {canEdit && (
                        <Button 
                          variant="text"
                          size="small"
                          icon={deleteIcon}
                          iconAlt="Delete"
                          appearance="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRule(index);
                          }}
                          className="delete-button"
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))
        ) : null}
        
        {/* Add Rule Input Field */}
        {canEdit && (
          <div className="list-item">
            <div className="rule-item list-item-inset add-rule-input-container">
              <div
                className="rule-content add-rule-input"
                contentEditable={true}
                suppressContentEditableWarning={true}
                data-placeholder="Add rule..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    const value = e.currentTarget.innerText;
                    handleAddRuleFromInput(value);
                    e.currentTarget.innerText = '';
                  }
                }}
                onBlur={(e) => {
                  const value = e.currentTarget.innerText;
                  if (value.trim()) {
                    handleAddRuleFromInput(value);
                    e.currentTarget.innerText = '';
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToneRulesSection; 